/**
 * The Impact tab's view model. FEAT-ATTRIB-003 `A2`.
 *
 * Separated from the JSX deliberately, for two reasons.
 *
 * **It is where the real risk lives.** The failure mode for a reporting panel
 * is not a crash; it is a number that renders, looks plausible, and is not the
 * number the service computed — a client-side round, a defaulted null, a share
 * derived from two other figures. None of those throw. All of them live in the
 * mapping from payload to display, which is this module and is exhaustively
 * tested.
 *
 * **It is the part that CAN be tested here.** `react-scripts`'s Jest resolver
 * does not honour Chakra's `exports` map (`@chakra-ui/utils/context` fails to
 * resolve from inside `@chakra-ui/react`), so no Chakra component in this app
 * can be rendered under test — which is why no existing test imports Chakra.
 * That is a pre-existing tooling limitation, not something this feature
 * introduced, and the honest response is to put the logic somewhere a test can
 * reach rather than to claim coverage the runner cannot give.
 *
 * ## The one rule
 *
 * **Nothing here derives one ledger from another.** Observed, credited and
 * residual are three permanently separate ledgers in the service, and
 * computing `residual = observed − credited` in the client would reintroduce
 * the conflation that separation exists to prevent — in a place no attribution
 * test can see. Every figure is read from its own field or reported absent.
 */

/** Minor units to a display string. Absent is an em dash, never `0.00`. */
export function money(minor, currency) {
    if (minor === null || minor === undefined) return '—';
    const n = Number(minor);
    if (!Number.isFinite(n)) return '—';
    return `${currency ? `${currency} ` : ''}${(n / 100).toLocaleString(undefined, {
        minimumFractionDigits: 2, maximumFractionDigits: 2,
    })}`;
}

/** A count. Absent is an em dash, never `0`. */
export function count(n) {
    if (n === null || n === undefined) return '—';
    const v = Number(n);
    return Number.isFinite(v) ? v.toLocaleString() : '—';
}

/**
 * A share as a percentage.
 *
 * Absent is an em dash and NEVER `0.0%`: a share nobody measured and a share
 * measured as zero are different facts, and the second is a finding while the
 * first is a gap.
 */
export function share(x) {
    if (x === null || x === undefined) return '—';
    const v = Number(x);
    return Number.isFinite(v) ? `${(v * 100).toFixed(1)}%` : '—';
}

/**
 * MICRO units to a display string. `DA-BUG-ATTR-0264`.
 *
 * Deliberately NOT `money()`. A cost arrives in micro — millionths of a
 * currency unit — and `money()` divides by 100, so passing a micro figure to
 * it reports 4,420,000 micro (PKR 4.42) as PKR 44,200.00.
 *
 * And the decimals matter more than usual. A measured cid=1 unit price is
 * 0.004420 and Pakistan utility WhatsApp is 0.005400, so a two-decimal format
 * renders both as `0.00` — a priced message reported as free, which is
 * `DA-BUG-ATTR-0197` reappearing in the view layer after the service went to
 * the trouble of keeping the precision. Up to SIX significant decimals, with
 * trailing zeros trimmed so PKR 4.42 does not read as PKR 4.420000.
 */
export function micro(v, currency) {
    if (v === null || v === undefined) return '—';
    const n = Number(v);
    if (typeof v !== 'number' || !Number.isFinite(n)) return '—';
    const units = n / 1_000_000;
    const text = units.toLocaleString(undefined, {
        minimumFractionDigits: 2, maximumFractionDigits: 6,
    });
    return `${currency ? `${currency} ` : ''}${text}`;
}

export const VIEW_STATE = Object.freeze({
    NOT_CONFIGURED: 'not_configured',
    REFUSED: 'refused',
    READY: 'ready',
});

/**
 * The payload into everything the panel displays.
 *
 * @param {object|null} payload   what `fetchImpactSummary` returned
 * @param {boolean} configured    whether the service has a base URL
 */
export function buildImpactView(payload, { configured = true } = {}) {
    if (!configured) {
        return {
            state: VIEW_STATE.NOT_CONFIGURED,
            message: 'Attribution is not configured for this deployment.',
            /** The variable is NAMED, because the failure this replaces was a
             *  404 against `undefined/...` with nothing identifying the cause. */
            envVar: 'REACT_APP_ATTRIBUTION_API_URL',
        };
    }

    if (!payload || payload.ok === false) {
        return {
            state: VIEW_STATE.REFUSED,
            /**
             * A refusal carries its own reason and showing it IS the product:
             * `label_not_permitted_in_this_state` means the tenant has not
             * earned a modelled number, which is the maturity gate working.
             */
            error: payload?.error ?? 'request_failed',
            message: payload?.message
                || payload?.detail
                || 'The service did not return a summary.',
        };
    }

    const d = payload.data ?? {};
    const meta = payload.meta ?? {};
    const disclosure = meta.disclosure ?? {};
    const observed = d.observed ?? {};
    const credited = d.credited ?? {};
    const residual = d.residual ?? {};
    const completeness = d.completeness ?? {};
    const currency = meta.currency || observed.currency || '';

    return {
        state: VIEW_STATE.READY,
        currency,
        evidenceLabel: meta.evidence_label ?? null,

        /* Three ledgers, three reads. No arithmetic between them. */
        observed: {
            conversions: count(observed.conversions),
            value: money(observed.value_minor, currency),
            unavailableReason: observed.available === false ? (observed.reason ?? null) : null,
        },
        credited: {
            share: share(credited.share),
            value: money(credited.value_minor, currency),
        },
        residual: {
            share: share(residual.share),
            value: money(residual.value_minor, currency),
            abstained: count(residual.abstained_conversions),
        },

        breakdown: (Array.isArray(d.breakdown) ? d.breakdown : []).map((row) => ({
            key: String(row.value ?? ''),
            label: String(row.value ?? ''),
            units: count(row.credited_units),
            value: money(row.credited_value_minor, currency),
        })),

        coverageUnavailableReason: completeness.available === false
            ? (completeness.reason ?? 'not stated')
            : null,

        /* MRC §2.3.4.3 — non-technical methodology disclosure. */
        disclosure: {
            modelUsed: disclosure.model_used || '—',
            reliance: disclosure.level_of_model_reliance || '—',
            /**
             * An empty source list is reported with its REASON rather than as
             * an empty list: "we tried and could not" and "this surface never
             * looks" are different facts, and the service distinguishes them.
             */
            sources: (disclosure.data_sources ?? []).length > 0
                ? disclosure.data_sources.join(', ')
                : (disclosure.state_reason || 'not stated'),
            /**
             * Presence BEFORE coercion. `Number(null)` is 0 and 0 is finite,
             * so `Number.isFinite(Number(x))` accepts an explicitly null
             * window as a window of ZERO DAYS — which reads as "we looked
             * back over no time at all", a claim nobody made. The fourth
             * instance of this exact trap in this capability.
             */
            lookbackDays: typeof disclosure.lookback_window_days === 'number'
                && Number.isFinite(disclosure.lookback_window_days)
                ? disclosure.lookback_window_days
                : null,
        },

        /**
         * Phase A's fourth figure — `DA-BUG-ATTR-0264`.
         *
         * The gate reads "conversions, credit by channel, residual AND cost
         * per attributed conversion", and this panel shipped the first three.
         * Read from its own block and never derived: dividing
         * `credited.value_minor` by anything here would mix attributed revenue
         * with send cost, two unrelated quantities in two different units.
         */
        cost: costBlock(d.attributed_cost, meta.cost_currency),

        /** MRC §2.3.4.4 — always carried, never filtered. */
        qualifiers: Array.isArray(disclosure.qualifiers) ? disclosure.qualifiers : [],

        /**
         * What this number IS and IS NOT, in plain words.
         *
         * Phase A ships credit as the headline, and the plan's condition for
         * that is "say so explicitly rather than quietly publishing credit as
         * if it were lift". A credit number and a lift number are identical
         * in units, shape and plausibility, so the statement has to be on the
         * panel — not only in the payload, and not only in a doc.
         *
         * `usable_for` is carried with it deliberately: a caveat that leaves
         * the reader believing the number is useless has failed differently.
         * The allocation is conserved, explainable and cost-joined, and it is
         * correct for what it measures.
         */
        causal: disclosure.causal
            ? {
                permitted: disclosure.causal.causal_claim_permitted === true,
                headlineIs: disclosure.causal.headline_is || null,
                notThis: disclosure.causal.not_this || null,
                usableFor: Array.isArray(disclosure.causal.usable_for)
                    ? disclosure.causal.usable_for : [],
                whatWouldChangeIt: disclosure.causal.what_would_change_it || null,
            }
            : null,
    };
}

/**
 * The attributed-cost block.
 *
 * A refusal is displayed with its REASON and its coverage, because at zero
 * coverage "0 of 4,312 credited sends could be priced, all of them
 * `no_active_billing_sheet`" is the actionable finding and a blank cell is
 * not. Partial coverage shows the figure AND says it is a lower bound: the
 * numerator covers the priced sends and the denominator every attributed
 * conversion, so the ratio understates, and the service deliberately does not
 * scale it up because that would assume the unpriced sends cost the same.
 */
export function costBlock(cost, currencyFromMeta) {
    if (!cost || typeof cost !== 'object') {
        return { available: false, reason: 'not_reported', perConversion: '—', coverage: '—', unpriced: [] };
    }

    const currency = cost.currency || currencyFromMeta || '';
    const unpriced = Object.entries(cost.unpriced_by_reason ?? {})
        .map(([reason, touches]) => ({ reason, touches: count(touches) }));

    if (cost.available !== true) {
        return {
            available: false,
            /** Named, never blank. A refusal with no reason is a dead end. */
            reason: cost.reason || 'not_stated',
            perConversion: '—',
            coverage: cost.coverage ? coverageText(cost.coverage) : '—',
            unpriced,
        };
    }

    return {
        available: true,
        reason: null,
        perConversion: micro(cost.cost_per_attributed_conversion_micro, currency),
        totalSpend: micro(cost.cost_of_credited_touches_micro, currency),
        conversions: count(cost.attributed_conversions),
        coverage: coverageText(cost.coverage),
        /**
         * Shown as a qualifier on the number itself, not as a footnote. A
         * lower bound presented as a point estimate is the kind of plausible
         * figure this whole module exists to prevent.
         */
        isLowerBound: cost.is_lower_bound === true,
        unpriced,
    };
}

/** `priced of total (share)`, with an unmeasured share as an em dash. */
function coverageText(coverage) {
    if (!coverage || typeof coverage !== 'object') return '—';
    return `${count(coverage.priced)} of ${count(coverage.total)} priced (${share(coverage.share)})`;
}
